import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type ReactNode,
} from "react";

// 3D tilt wrapper, ported from pokemon-cards-css. The transform lives
// on `inner` (not the wrapper) so an ancestor's getBoundingClientRect
// returns the un-tilted layout box — required for the pack-opening
// FLIP fly-origin. `reset()` snaps tilt to 0 right before measuring.

export interface CardTiltHandle {
  reset(): void;
}

interface CardTiltProps {
  enabled?: boolean;
  children: ReactNode;
}

const MAX_TILT_DEG = 14;

export const CardTilt = forwardRef<CardTiltHandle, CardTiltProps>(
  function CardTilt({ enabled = true, children }, ref) {
    const inner = useRef<HTMLDivElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        reset() {
          const node = inner.current;
          if (!node) return;
          node.style.setProperty("--tilt-x", "0deg");
          node.style.setProperty("--tilt-y", "0deg");
        },
      }),
      [],
    );

    useEffect(() => {
      if (!enabled) return;
      const node = inner.current;
      if (!node) return;
      const onMove = (e: MouseEvent) => {
        const r = node.getBoundingClientRect();
        if (r.width === 0 || r.height === 0) return;
        const cx = (e.clientX - r.left) / r.width - 0.5;
        const cy = (e.clientY - r.top) / r.height - 0.5;
        // rotateY sign is flipped so the cursor pulls the closer edge
        // forward (card tips *toward* the cursor).
        node.style.setProperty("--tilt-y", `${(-cx * 2 * MAX_TILT_DEG).toFixed(2)}deg`);
        node.style.setProperty("--tilt-x", `${(cy * 2 * MAX_TILT_DEG).toFixed(2)}deg`);
      };
      const onLeave = () => {
        node.style.setProperty("--tilt-x", "0deg");
        node.style.setProperty("--tilt-y", "0deg");
      };
      node.addEventListener("mousemove", onMove);
      node.addEventListener("mouseleave", onLeave);
      return () => {
        node.removeEventListener("mousemove", onMove);
        node.removeEventListener("mouseleave", onLeave);
      };
    }, [enabled]);

    if (!enabled) return <>{children}</>;

    // `display: block` so the cqw-based Card (width:100%) can resolve
    // against the parent's explicit width. inline-block here would
    // shrink-wrap to the child's intrinsic width — and the child has
    // none, so the card collapses to a few pixels.
    return (
      <div style={{ perspective: "1200px" }}>
        <div
          ref={inner}
          data-testid="card-tilt"
          style={{
            transform:
              "rotateY(var(--tilt-y, 0deg)) rotateX(var(--tilt-x, 0deg))",
            transformStyle: "preserve-3d",
            willChange: "transform",
          }}
        >
          {children}
        </div>
      </div>
    );
  },
);
